import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getMemberDetail, updateMember, getSpiritualFathers, getOrdainedAuthorities, getNationalities } from '../../services/api';
import { mergeNationalities } from '../../data/nationalities';
import Toast from '../../components/Toast';
import PhoneInput from '../../components/PhoneInput';

const currentYear = new Date().getFullYear();

const EditMember = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [leaders, setLeaders] = useState([]);
  const [authorities, setAuthorities] = useState([]);
  const [nationalities, setNationalities] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };
  const set = f => e => setForm(p => ({ ...p, [f]: e.target.value }));

  const titleToGender = { 'Mr.': 'male', 'Mrs.': 'female', 'Priest': 'male', 'Deacon': 'male' };
  const setTitle = (e) => {
    const title = e.target.value;
    setForm(p => ({ ...p, title, gender: titleToGender[title] || p.gender }));
  };

  useEffect(() => {
    getMemberDetail(id).then(r => {
      const m = r.data;
      if (!m) return;
      // Backend stores titles uppercase (PRIEST); the dropdown options are
      // title-case (Priest), so normalize on load or the dropdown shows blank.
      const TITLE_MAP = { 'mr.': 'Mr.', 'ms.': 'Ms.', 'mrs.': 'Mrs.', 'priest': 'Priest', 'deacon': 'Deacon' };
      const normalizedTitle = TITLE_MAP[String(m.title || '').trim().toLowerCase()] || m.title || '';
      setForm({
        title: normalizedTitle,
        first_name: m.first_name || '',
        middle_name: m.middle_name || '',
        last_name: m.last_name || '',
        christian_name: m.christian_name || '',
        gender: m.gender || '',
        birth_year: m.birth_year || '',
        marital_status: m.marital_status || '',
        occupation: m.occupation || '',
        phone: m.phone || '',
        whatsapp: m.whatsapp || '',
        email: m.email || '',
        address: m.address || '',
        holy_communion: m.holy_communion || 'unknown',
        membership_status: m.membership_status || 'member',
        spiritual_father_id: m.spiritual_father_id || '',
        ordination_date: m.ordination_date || '',
        ordained_by: m.ordained_by || '',
        nationality: m.nationality || '',
      });
    }).catch(() => {});
    getSpiritualFathers().then(r => setLeaders(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    getOrdainedAuthorities().then(r => setAuthorities(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    getNationalities().then(r => setNationalities(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [id]);

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.first_name) return setError('First name is required');
    if (!form.last_name)  return setError('Last name is required');
    setSaving(true);
    try {
      await updateMember(id, form);
      showToast('Member updated');
      setTimeout(() => navigate(`/members/profile/${id}`), 1000);
    } catch (err) { setError(err.response?.data?.error || 'Failed to save'); }
    finally { setSaving(false); }
  };

  if (!form) return <div className="page-content" style={{ color: '#6b7280' }}>Loading...</div>;

  return (
    <div className="page-content">
      <div className="page-header">
        <div><h1>{t('members.editMember')}</h1></div>
        <button className="btn btn-secondary" onClick={() => navigate(`/members/profile/${id}`)}>← {t('common.back')}</button>
      </div>

      <form onSubmit={handleSave}>
        {error && <div className="error-msg" style={{ marginBottom: 16 }}>{error}</div>}

        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header">{t('members.personalInfo')}</div>
          <div className="card-body">
            <div className="form-grid">
              <div className="form-group">
                <label>{t('members.title')}</label>
                <select value={form.title} onChange={setTitle}>
                  <option value="">—</option>
                  <option value="Mr.">Mr.</option>
                  <option value="Ms.">Ms.</option>
                  <option value="Mrs.">Mrs.</option>
                  <option value="Dr.">Dr.</option>
                  <option value="Priest">Priest</option>
                  <option value="Deacon">Deacon</option>
                </select>
              </div>
              {(form.title === 'Priest' || form.title === 'Deacon') && (
                <>
                  <div className="form-group">
                    <label>Date of Ordination</label>
                    <input type="date" value={form.ordination_date || ''} onChange={set('ordination_date')} />
                  </div>
                  <div className="form-group">
                    <label>Ordained By</label>
                    <input
                      list="ordained-by-list"
                      value={form.ordained_by || ''}
                      onChange={set('ordained_by')}
                      placeholder="Bishop / authority who ordained"
                    />
                    <datalist id="ordained-by-list">
                      {authorities.map(a => <option key={a} value={a} />)}
                    </datalist>
                  </div>
                </>
              )}
              <div className="form-group">
                <label>{t('members.firstName')} <span className="required">*</span></label>
                <input value={form.first_name} onChange={set('first_name')} />
              </div>
              <div className="form-group">
                <label>{t('members.middleName')}</label>
                <input value={form.middle_name} onChange={set('middle_name')} />
              </div>
              <div className="form-group">
                <label>{t('members.lastName')} <span className="required">*</span></label>
                <input value={form.last_name} onChange={set('last_name')} />
              </div>
              <div className="form-group">
                <label>{t('members.christianName')}</label>
                <input value={form.christian_name} onChange={set('christian_name')} />
              </div>
              <div className="form-group">
                <label>{t('members.gender')}</label>
                <select value={form.gender} onChange={set('gender')}>
                  <option value="">—</option>
                  <option value="male">{t('members.male')}</option>
                  <option value="female">{t('members.female')}</option>
                </select>
              </div>
              <div className="form-group">
                <label>{t('members.yearOfBirth')} {form.birth_year && !isNaN(form.birth_year) && <span style={{ fontWeight: 400, color: '#4f46e5', marginLeft: 8 }}>→ {t('members.age')}: {currentYear - parseInt(form.birth_year)}</span>}</label>
                <input type="number" value={form.birth_year} onChange={set('birth_year')} min="1900" max={currentYear} />
              </div>
              <div className="form-group">
                <label>{t('members.maritalStatus')}</label>
                <select value={form.marital_status} onChange={set('marital_status')}>
                  <option value="">—</option>
                  <option value="Single">{t('members.single')}</option>
                  <option value="Married">{t('members.married')}</option>
                  <option value="Widowed">{t('members.widowed')}</option>
                  <option value="Divorced">{t('members.divorced')}</option>
                </select>
              </div>
              <div className="form-group">
                <label>{t('members.occupation')}</label>
                <input value={form.occupation} onChange={set('occupation')} />
              </div>
              <div className="form-group">
                <label>Nationality</label>
                <input
                  list="nationality-list"
                  value={form.nationality || ''}
                  onChange={set('nationality')}
                  placeholder="e.g. Zambian, Ethiopian"
                />
                <datalist id="nationality-list">
                  {mergeNationalities(nationalities).map(n => <option key={n} value={n} />)}
                </datalist>
              </div>
              <div className="form-group">
                <label>{t('common.phone')}</label>
                <PhoneInput value={form.phone} onChange={v => setForm(p => ({ ...p, phone: v }))} />
              </div>
              <div className="form-group">
                <label>WhatsApp</label>
                <PhoneInput value={form.whatsapp} onChange={v => setForm(p => ({ ...p, whatsapp: v }))} />
              </div>
              <div className="form-group">
                <label>{t('common.email')}</label>
                <input type="email" value={form.email} onChange={set('email')} />
              </div>
              <div className="form-group">
                <label>{t('members.holyCommunion')}</label>
                <select value={form.holy_communion} onChange={set('holy_communion')}>
                  <option value="unknown">—</option>
                  <option value="taking">{t('members.taking')}</option>
                  <option value="not_taking">{t('members.notTaking')}</option>
                  <option value="excommunicated">{t('members.excommunicated')}</option>
                </select>
              </div>
              <div className="form-group form-full">
                <label>{t('common.address')}</label>
                <input value={form.address} onChange={set('address')} />
              </div>
            </div>
          </div>
        </div>

        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header">{t('nav.spiritualCare')}</div>
          <div className="card-body">
            <div className="form-group">
              <label>{t('members.spiritualFather')}</label>
              <select value={form.spiritual_father_id} onChange={set('spiritual_father_id')}>
                <option value="">— {t('members.unassigned')} —</option>
                {leaders.filter(l => String(l.id) !== String(id)).map(l => (
                  <option key={l.id} value={l.id}>{l.first_name} {l.last_name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? t('common.saving') : t('common.save')}</button>
          <button type="button" className="btn btn-secondary" onClick={() => navigate(`/members/profile/${id}`)}>{t('common.cancel')}</button>
        </div>
      </form>
      <Toast message={toast?.msg} type={toast?.type} onClose={() => setToast(null)} />
    </div>
  );
};

export default EditMember;
